import { useCallback, useMemo } from 'react'
import type { JSX } from 'react'

import { resolveLocale } from '@lensmira/i18n'

import { LocaleProvider } from '../i18n.js'
import { Portal } from './Portal.js'
import {
  readPortal,
  readPortalOrder,
  readPortalPrescriptionCopy,
  type PortalConfig,
} from './portal-reads.js'
import { ReadFailed } from '../api/reads.js'

/**
 * The portal's entry: a link in, a page out, with no staff session shell around it. `CUS-007`.
 *
 * The link is `/portal/<tenantRef>#<token>`. The tenant is in the path because the server has to
 * resolve it through the directory before it can look the token up (`TEN-001`), and the token is in
 * the fragment because a browser never sends a fragment: not to the server, not in an access log,
 * not in a `Referer`. A link with either half missing gets the same sentence as a link that does
 * not work, because to the person holding it that is what has happened.
 *
 * The language is the browser's, since a customer has no account to hold a preference. A language
 * the product does not ship falls through to English (`resolveLocale`), and a message with no Urdu
 * yet falls back to English per key, so the page degrades to something readable instead of blank.
 */

const API_BASE_URL = '/api'

export interface PortalAppProps {
  /** `location.pathname`. */
  readonly pathname: string
  /** `location.hash`, including the leading `#`. */
  readonly hash: string
  readonly doFetch?: typeof globalThis.fetch
  /** `navigator.language`. */
  readonly language?: string
  /** The device's zone. */
  readonly timezone?: string
}

/** `/portal/<tenantRef>` to the tenant reference, or null. */
export function tenantRefOf(pathname: string): string | null {
  const match = /^\/portal\/([^/]+)\/?$/.exec(pathname)
  if (match?.[1] === undefined) return null
  try {
    return decodeURIComponent(match[1])
  } catch {
    return null
  }
}

export function PortalApp({
  pathname,
  hash,
  doFetch,
  language,
  timezone,
}: PortalAppProps): JSX.Element {
  const locale = resolveLocale({ user: language ?? null })
  const tenantRef = tenantRefOf(pathname)
  const token = hash.startsWith('#') ? hash.slice(1) : hash
  const fetchImpl = useMemo(() => doFetch ?? globalThis.fetch.bind(globalThis), [doFetch])

  const config: PortalConfig | null = useMemo(
    () =>
      tenantRef === null || token === ''
        ? null
        : { baseUrl: API_BASE_URL, tenantRef, token, doFetch: fetchImpl },
    [tenantRef, token, fetchImpl],
  )

  const load = useCallback(
    () =>
      config === null
        ? // No request at all: there is nothing to look up. Shaped like the server's own answer so
          // the page says the same sentence it says for a link that was refused.
          Promise.reject(new ReadFailed(404, 'PORTAL_LINK_UNUSABLE', 'This link does not work.'))
        : readPortal(config),
    [config],
  )
  const loadOrder = useCallback(
    (orderId: string) =>
      config === null
        ? Promise.reject(new ReadFailed(404, 'PORTAL_LINK_UNUSABLE', 'This link does not work.'))
        : readPortalOrder(config, orderId),
    [config],
  )
  const loadCopy = useCallback(
    (prescriptionId: string) =>
      config === null
        ? Promise.reject(new ReadFailed(404, 'PORTAL_LINK_UNUSABLE', 'This link does not work.'))
        : readPortalPrescriptionCopy(config, prescriptionId),
    [config],
  )

  return (
    <LocaleProvider locale={locale}>
      <Portal
        load={load}
        loadOrder={loadOrder}
        loadCopy={loadCopy}
        timezone={timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone}
      />
    </LocaleProvider>
  )
}
